"use client";

import type { BillingPlan } from "@pex/billing";
import { Button, Switch } from "@pex/ui-web";
import Link from "next/link";
import { Suspense, useEffect, useState } from "react";
import {
  type BillingPeriod,
  COMMUNITY_PLANS,
  findDbPlan,
  type PlanCardCopy,
  PROGRAMME_PLANS,
  resolvePricing,
} from "@/lib/plan-copy";
import { LegacyOpenCheckoutRedirect } from "../_components/legacy-open-checkout-redirect";
import { getPricingCtaState, type PricingCtaState } from "../actions";

type PlanCategory = "community" | "programme";

// ─── CTA resolution (auth matrix — D1) ───────────────────────────────────────

/**
 * Client-side CTA state: the server's PricingCtaState plus a "resolving"
 * phase while the auth island is in flight. Purchase-path clicks always go
 * to the dedicated /checkout page, which re-resolves the visitor's state
 * server-side — so resolving/signed-out/purchase all share the same safe
 * link and buttons are clickable immediately. Only the org-holder and
 * card-on-file CTAs swap in once the island resolves.
 */
type CtaState = PricingCtaState | { kind: "resolving" };

const BRIDGE_URL = process.env.NEXT_PUBLIC_BRIDGE_URL ?? "";

/** Bridge's create-org picker with the right tab pre-selected (card-on-file path). */
function bridgeCreateOrgHref(cardKey: string, billing: BillingPeriod): string {
  return `${BRIDGE_URL}/create-org?plan=${cardKey}&billing=${billing}`;
}

// ─── Plan card ────────────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      className="mt-0.5 h-4 w-4 shrink-0 text-foreground"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      viewBox="0 0 24 24"
    >
      <path
        d="M4.5 12.75l6 6 9-13.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PlanCard({
  billing,
  card,
  cta,
  plans,
}: {
  billing: BillingPeriod;
  card: PlanCardCopy;
  cta: CtaState;
  plans: BillingPlan[];
}) {
  const pricing = resolvePricing(plans, card);
  const price = billing === "monthly" ? pricing.monthly : pricing.annualMonthly;
  const dbPlan = findDbPlan(plans, card.key, billing);

  let action: React.ReactNode;
  switch (cta.kind) {
    case "manage":
      action = (
        <Button asChild className="w-full" variant="fancy">
          <a href={`${BRIDGE_URL}/subscription`}>Manage your plan in Bridge</a>
        </Button>
      );
      break;
    case "reactivate":
      action = (
        <Button asChild className="w-full" variant="fancy">
          <a href={`${BRIDGE_URL}/subscription/plans`}>
            Reactivate your plan in Bridge
          </a>
        </Button>
      );
      break;
    case "card-on-file":
      action = (
        <Button asChild className="w-full" variant="fancy">
          <a href={bridgeCreateOrgHref(card.key, billing)}>Get Started</a>
        </Button>
      );
      break;
    default:
      // resolving | signed-out | purchase — the /checkout page re-resolves
      // the visitor's state server-side and routes them correctly, so this
      // link is safe to click before (and after) the island resolves. With
      // no DB plan there is nothing to check out — fall back to the legacy
      // Bridge plan-picker round trip.
      action = dbPlan?.processor_plan_id ? (
        <Button asChild className="w-full" variant="fancy">
          <Link
            href={`/checkout?plan=${encodeURIComponent(dbPlan.processor_plan_id)}`}
          >
            Get Started
          </Link>
        </Button>
      ) : (
        <Button asChild className="w-full" variant="fancy">
          <a href={bridgeCreateOrgHref(card.key, billing)}>Get Started</a>
        </Button>
      );
  }

  return (
    <div className="flex flex-col rounded-2xl border bg-card p-6 shadow-sm">
      <h3 className="heading-5 mb-1 font-bold">{card.name}</h3>
      <div className="mb-1">
        <span className="font-bold text-3xl">{price}</span>
        <span className="body-sm text-muted-foreground">/mo</span>
      </div>
      {billing === "annual" ? (
        <p className="body-sm mb-6 font-medium text-amber-600">
          {pricing.annualBilled} billed annually
        </p>
      ) : (
        <p className="body-sm mb-6 text-muted-foreground">
          or {pricing.annualMonthly}/mo, {pricing.annualBilled} billed annually
        </p>
      )}
      <ul className="mb-8 flex-1 space-y-2">
        {card.features.map((feature) => (
          <li className="body-base flex items-start gap-2" key={feature}>
            <CheckIcon />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      {action}
    </div>
  );
}

// ─── Section ──────────────────────────────────────────────────────────────────

export function PricingPlans({ plans }: { plans: BillingPlan[] }) {
  const [billing, setBilling] = useState<BillingPeriod>("monthly");
  const [category, setCategory] = useState<PlanCategory>("community");

  const [cta, setCta] = useState<CtaState>({ kind: "resolving" });

  // Auth island (D2): the page is static; per-user CTA state arrives client-side.
  useEffect(() => {
    let cancelled = false;
    getPricingCtaState()
      .then((state) => {
        if (!cancelled) {
          setCta(state);
        }
      })
      .catch(() => {
        // Fall back to the default checkout link (safe for every state)
        if (!cancelled) {
          setCta({ kind: "signed-out" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const cards = category === "community" ? COMMUNITY_PLANS : PROGRAMME_PLANS;

  return (
    <section className="bg-white py-20" id="getting-started_pricing">
      {/* Pre-/checkout sign-up round trips land here with ?openCheckout — forward them */}
      <Suspense fallback={null}>
        <LegacyOpenCheckoutRedirect />
      </Suspense>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-14 max-w-3xl space-y-6 text-center">
          <h2 className="heading-2 font-bold">Pricing Plans</h2>
          <p className="body-lg text-muted-foreground">
            Choose a plan that suits your community needs
          </p>

          {/* Monthly / Annual toggle — equal-width labels keep the switch centred */}
          <div className="flex items-center justify-center gap-3">
            <span
              className={`w-20 text-right font-medium text-sm transition-colors ${billing === "monthly" ? "text-foreground" : "text-muted-foreground"}`}
            >
              Monthly
            </span>
            <Switch
              checked={billing === "annual"}
              onCheckedChange={(checked) =>
                setBilling(checked ? "annual" : "monthly")
              }
            />
            <span
              className={`w-20 font-medium text-sm transition-colors ${billing === "annual" ? "text-foreground" : "text-muted-foreground"}`}
            >
              Annually
            </span>
          </div>
          {/* Save 20% hint — shown below toggle so it doesn't offset the labels */}
          <p
            className={`-mt-2 font-medium text-amber-600 text-xs transition-opacity duration-200 ${billing === "annual" ? "opacity-100" : "opacity-0"}`}
          >
            Save 20% with annual billing
          </p>

          {/* Groups & Programmes / Programmes Only — charcoal active pill, no border */}
          <div className="flex justify-center gap-1">
            <button
              className={`rounded-full px-5 py-2 font-medium text-sm transition-all duration-200 ${
                category === "community"
                  ? "bg-[rgb(24,24,24)] text-white"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setCategory("community")}
              type="button"
            >
              Groups &amp; Programmes
            </button>
            <button
              className={`rounded-full px-5 py-2 font-medium text-sm transition-all duration-200 ${
                category === "programme"
                  ? "bg-[rgb(24,24,24)] text-white"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setCategory("programme")}
              type="button"
            >
              Programmes Only
            </button>
          </div>
        </div>

        <div className="grid gap-6 sm:grid-cols-3">
          {cards.map((card) => (
            <PlanCard
              billing={billing}
              card={card}
              cta={cta}
              key={card.name}
              plans={plans}
            />
          ))}
        </div>

        <p className="body-sm mt-8 text-center text-muted-foreground">
          *Unlimited programme groups can be created for each active specialised
          programme, with a once-off digital workbook fee per participant
          applicable.
        </p>
      </div>
    </section>
  );
}
